import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { metrics } from '@opentelemetry/api';

let meterProvider: MeterProvider | null = null;
let tracerProvider: WebTracerProvider | null = null;

// GPC decision: OTLP collects operational telemetry (error rates, performance,
// service health). Resource attributes include browser family for debugging, but no
// account-level identifiers or behavioral profiles. Not "sale or sharing" under CCPA,
// so not subject to GPC. Revisit if user IDs, session IDs, or cross-site tracking
// attributes are added.
export function initTelemetry(): void {
  const otlpUrl = import.meta.env.VITE_OTLP_URL || '/v1';

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'lexicontest-client',
    [ATTR_SERVICE_VERSION]: import.meta.env.VITE_APP_VERSION,
    'browser.family': detectBrowserFamily(),
  });

  try {
    const metricExporter = new OTLPMetricExporter({
      url: `${otlpUrl}/metrics`,
    });

    meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter: metricExporter,
          exportIntervalMillis: 30_000,
        }),
      ],
    });

    metrics.setGlobalMeterProvider(meterProvider);
  } catch (error) {
    console.warn('[Telemetry] Failed to initialize MeterProvider:', error);
  }

  try {
    const traceExporter = new OTLPTraceExporter({
      url: `${otlpUrl}/traces`,
    });

    tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [new BatchSpanProcessor(traceExporter)],
    });

    tracerProvider.register();
  } catch (error) {
    console.warn('[Telemetry] Failed to initialize TracerProvider:', error);
  }
}

function detectBrowserFamily(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Safari/')) return 'Safari';
  return 'Other';
}

export async function shutdownTelemetry(): Promise<void> {
  const promises: Promise<void>[] = [];

  if (meterProvider) {
    promises.push(
      meterProvider.forceFlush().catch(err => console.warn('[Telemetry] Metric flush failed:', err))
    );
  }
  if (tracerProvider) {
    promises.push(
      tracerProvider.forceFlush().catch(err => console.warn('[Telemetry] Trace flush failed:', err))
    );
  }

  await Promise.all(promises);
}
